import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  MenuItem,
  Select,
  SvgIcon,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useSelector } from 'react-redux';
import { ReactComponent as CrossIcon } from '@/assets/icons/cross.svg';
import { Inspection } from '@/types/Inspection.type';
import { Image } from '@/types/Image.type';
import { InspectionImageType } from '@/enums/InspectionImage.enum';
import { imageStorage } from '@/store/idbStorage/imageStorage';
import {
  getAvailableInspectionTemplate,
  getSizeChartVersion,
  hasInspectionSizeChart,
  restructureSizeChartData,
} from '@/utils/inspection.utils';
import { DefectDetailsTable } from '@/features/components/DefectDetails/DefectDetailsTable';
import { generateUniqueId } from '@/utils/string.util';
import { RootState } from '@/store/rootReducer';
import { MeasurementSize } from '@/features/inspection-details/components/record-measurement/components/MeasurementSize';
import { useImageCategories } from '@/hooks/masterData';
import { filterImageCategoriesByTemplate } from '@/utils/image-categories.util';
import { useTemplate } from '@/hooks/useTemplate';
import styles from './Preview.module.scss';
import { getChecklistModuleNames, parseCheckedModules } from '@/utils/checked-modules.util';
import { useCheckpointChecks } from '@/hooks/masterData';
import {
  getActiveChecksForCheckpoint,
  getCheckpointCheckTranslationKey,
  hasNestedCheckpointChecks,
  isCheckpointCheckChecked,
  isCheckpointComplete,
} from '@/utils/checkpoint-checks.util';

interface ChecklistPreviewChip {
  key: string;
  label: string;
  isChecked: boolean;
}

interface ChecklistPreviewGroup {
  key: string;
  parent: ChecklistPreviewChip;
  children: ChecklistPreviewChip[];
}

const DEFAULT_IMAGE_TYPE_LABEL = 'Full Product Image';
const ALL_IMAGE_TYPES = '__ALL__';

function getImageTypeLabel(img: Image): string {
  if (typeof img.imageType === 'string' && img.imageType.trim()) {
    return img.imageType;
  }
  if (img.url) {
    try {
      const urlObj = new URL(img.url);
      const fromUrl = urlObj.searchParams.get('imageType');
      if (fromUrl) return fromUrl;
    } catch {
      // ignore invalid URL
    }
  }
  return DEFAULT_IMAGE_TYPE_LABEL;
}

function getImageSrc(img: Image): string | undefined {
  return img.url ?? img.uploadedUrl ?? (img.data as string | undefined);
}

const PreviewImage: React.FC<{ img: Image; inspectionId: string; showLabel?: boolean }> = ({
  img,
  inspectionId,
  showLabel = true,
}) => {
  const [src, setSrc] = useState<string | undefined>(() => getImageSrc(img));
  useEffect(() => {
    const initial = getImageSrc(img);
    if (initial) {
      setSrc(initial);
    } else if (inspectionId && img.id) {
      imageStorage.getImage(inspectionId, InspectionImageType.FULL_PRODUCT, img.id).then(setSrc);
    }
  }, [img.id, img.url, img.uploadedUrl, img.data, inspectionId]);
  return (
    <div className={styles.photoCard}>
      <div className={styles.photoWrap}>
        {src ? (
          <img src={src} alt="" className={styles.photo} />
        ) : (
          <div className={styles.photoPlaceholder} />
        )}
      </div>
      {showLabel && <span className={styles.photoLabel}>{getImageTypeLabel(img)}</span>}
    </div>
  );
};

export interface PreviewProps {
  inspection: Inspection | null | undefined;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  headerIcon?: React.ReactNode;
  showCloseIcon?: boolean;
  /** Optional footer content (e.g. action buttons). Rendered in a slot below the main content. */
  footer?: React.ReactNode;
}

export const Preview: React.FC<PreviewProps> = ({
  inspection,
  onClose,
  title,
  subtitle,
  headerIcon,
  showCloseIcon = true,
  footer,
}) => {
  const { t: translate } = useTranslation();
  const inspectionsData = useSelector((state: RootState) => state.inspectionsData);
  const config = useSelector((state: RootState) => state.appConfig);
  const { imageCategories } = useImageCategories();
  const templateImageCategories = useMemo(
    () => filterImageCategoriesByTemplate(imageCategories, inspection?.template?.id),
    [imageCategories, inspection?.template?.id]
  );
  const { checkpointChecks } = useCheckpointChecks();
  const [expandedMeasurementCard, setExpandedMeasurementCard] = useState<string | null>(null);
  const [selectedImageType, setSelectedImageType] = useState<string>(ALL_IMAGE_TYPES);

  if (!inspection) return null;

  const template = useTemplate(inspection);

  const defects = inspection.qcDefects || [];

  const inspectionImages = useMemo(() => {
    const fp = inspection.fpImages ?? [];
    const urlString = inspection.fullProductImageUrls?.trim();
    const urls = urlString
      ? urlString
          .split(',')
          .map((u) => u.trim())
          .filter(Boolean)
      : [];
    const urlImages: Image[] = urls.map(
      (url): Image => ({
        id: generateUniqueId(),
        url,
        type: InspectionImageType.FULL_PRODUCT,
      })
    );
    const newImgs = (inspection.newImages ?? []).filter((i) => !i.isDeleted);
    if (fp.length > 0) return fp;
    return [...urlImages, ...newImgs];
  }, [inspection.fpImages, inspection.fullProductImageUrls, inspection.newImages]);

  const sortedInspectionImages = useMemo(() => {
    const orderMap = new Map<string, number>();
    for (const cat of templateImageCategories) {
      if (cat?.imageCategoryName) {
        orderMap.set(
          cat.imageCategoryName,
          Number.isFinite(cat.displayOrder) ? cat.displayOrder : 9999
        );
      }
    }
    if (!orderMap.has(DEFAULT_IMAGE_TYPE_LABEL)) orderMap.set(DEFAULT_IMAGE_TYPE_LABEL, 0);

    return inspectionImages
      .map((img, idx) => {
        const label = getImageTypeLabel(img);
        const order = orderMap.get(label) ?? 10000;
        return { img, idx, label, order };
      })
      .sort((a, b) => {
        if (a.order !== b.order) return a.order - b.order;
        const labelCompare = a.label.localeCompare(b.label);
        if (labelCompare !== 0) return labelCompare;
        return a.idx - b.idx;
      })
      .map((x) => x.img);
  }, [inspectionImages, templateImageCategories]);

  const inspectionImageTypeOptions = useMemo(() => {
    const presentLabels = new Set(sortedInspectionImages.map(getImageTypeLabel));
    presentLabels.add(DEFAULT_IMAGE_TYPE_LABEL);

    const orderedFromMaster = [...templateImageCategories]
      .sort((a, b) => (a.displayOrder ?? 9999) - (b.displayOrder ?? 9999))
      .map((c) => c.imageCategoryName)
      .filter((name): name is string => !!name && presentLabels.has(name));

    const remaining = [...presentLabels]
      .filter((name) => !orderedFromMaster.includes(name))
      .sort((a, b) => a.localeCompare(b));

    return [
      { value: ALL_IMAGE_TYPES, label: translate('All') },
      ...orderedFromMaster.map((name) => ({ value: name, label: name })),
      ...remaining.map((name) => ({ value: name, label: name })),
    ];
  }, [sortedInspectionImages, templateImageCategories, translate]);

  const visibleInspectionImages = useMemo(() => {
    if (selectedImageType === ALL_IMAGE_TYPES) return sortedInspectionImages;
    return sortedInspectionImages.filter((img) => getImageTypeLabel(img) === selectedImageType);
  }, [sortedInspectionImages, selectedImageType]);

  const hasInspectionImagesModule = useMemo(() => {
    return (template?.modules ?? []).some(
      (m) => m.type === 'module' && m.name === 'InspectionImages'
    );
  }, [template]);

  const checkedModuleSet = useMemo(() => {
    return new Set(parseCheckedModules(inspection.checkedModules));
  }, [inspection.checkedModules]);

  const sizeCharts = inspectionsData?.sizeCharts ?? [];

  const hasSizeChart = useMemo(
    () =>
      hasInspectionSizeChart({
        measurements: inspection.measurements ?? [],
        sizeCharts,
        productId: inspection.productId,
      }),
    [inspection.measurements, inspection.productId, sizeCharts]
  );

  const availableTemplate = useMemo(
    () =>
      getAvailableInspectionTemplate(template, {
        measurements: inspection.measurements ?? [],
        sizeCharts,
        productId: inspection.productId,
      }),
    [template, inspection.measurements, inspection.productId, sizeCharts]
  );

  const measurementCards = useMemo(() => {
    const inspectionMeasurements = inspection.measurements ?? [];
    const requiredSizeChart = getSizeChartVersion(
      inspectionMeasurements,
      sizeCharts,
      inspection.productId
    );
    const passCount = config?.['size-check']?.passes || 3;
    const allCards = restructureSizeChartData(requiredSizeChart, inspectionMeasurements, passCount);
    return Object.fromEntries(
      Object.entries(allCards).filter(([, measurements]) =>
        measurements.some((m) => m.measurement != null && m.measurement !== '')
      )
    );
  }, [config, inspection.measurements, inspection.productId, sizeCharts]);

  const handleMeasurementCardToggle = (id: string | null) => {
    setExpandedMeasurementCard((prev) => (prev === id ? null : id));
  };

  const checklistItems = useMemo((): ChecklistPreviewGroup[] => {
    if (!availableTemplate) return [];
    const checklistModuleNames = getChecklistModuleNames(availableTemplate);
    const templateModules = availableTemplate.modules ?? [];
    const templateId = inspection.template?.id;
    return checklistModuleNames.map((moduleName) => {
      const module = templateModules.find((entry) => entry.name === moduleName);
      const moduleLabel = module?.header || moduleName;
      if (hasNestedCheckpointChecks(checkpointChecks ?? [], templateId, moduleName)) {
        const nested = getActiveChecksForCheckpoint(checkpointChecks ?? [], templateId, moduleName);
        return {
          key: moduleName,
          parent: {
            key: moduleName,
            label: moduleLabel,
            isChecked: isCheckpointComplete({
              checks: checkpointChecks ?? [],
              results: inspection.checkpointCheckResults,
              templateId,
              checkpointCode: moduleName,
            }),
          },
          children: nested
            .filter((check) => !check.isParent)
            .map((check) => ({
              key: check.id,
              label: getCheckpointCheckTranslationKey(check),
              isChecked: isCheckpointCheckChecked(inspection.checkpointCheckResults, check.id),
            })),
        };
      }
      return {
        key: moduleName,
        parent: {
          key: moduleName,
          label: moduleLabel,
          isChecked: checkedModuleSet.has(moduleName),
        },
        children: [],
      };
    });
  }, [
    availableTemplate,
    checkpointChecks,
    checkedModuleSet,
    inspection.checkpointCheckResults,
    inspection.template?.id,
  ]);

  const showChecklistPreviewSection = checklistItems.length > 0;
  const firstExpandedSection = showChecklistPreviewSection
    ? 'checklist'
    : hasSizeChart
      ? 'measurements'
      : 'images';

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <div className={styles.headerContent}>
            {headerIcon}
            <div>
              <h2 className={styles.title}>{title ?? translate('Inspection Preview')}</h2>
              {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
            </div>
          </div>
          {showCloseIcon && (
            <button
              type="button"
              className={styles.closeButton}
              onClick={onClose}
              aria-label={translate('Close')}
            >
              <SvgIcon component={CrossIcon} />
            </button>
          )}
        </div>
        <div className={styles.content}>
          {showChecklistPreviewSection && (
            <Accordion
              defaultExpanded={firstExpandedSection === 'checklist'}
              className={styles.accordion}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} className={styles.accordionSummary}>
                <h3 className={styles.sectionTitle}>{translate('Checklist')}</h3>
              </AccordionSummary>
              <AccordionDetails className={styles.accordionDetails}>
                <div className={styles.checklistList}>
                  {checklistItems.map((group) => {
                    const hasChildren = group.children.length > 0;
                    return (
                      <div
                        key={group.key}
                        className={hasChildren ? styles.checklistGroup : undefined}
                        role={hasChildren ? 'group' : undefined}
                        aria-label={hasChildren ? translate(group.parent.label) : undefined}
                      >
                        <span
                          className={
                            group.parent.isChecked
                              ? `${styles.checklistChip} ${styles.checklistChipChecked}`
                              : `${styles.checklistChip} ${styles.checklistChipUnchecked}`
                          }
                        >
                          {translate(group.parent.label)}
                        </span>
                        {group.children.map((child) => (
                          <span
                            key={child.key}
                            className={
                              child.isChecked
                                ? `${styles.checklistChip} ${styles.checklistChipChecked}`
                                : `${styles.checklistChip} ${styles.checklistChipUnchecked}`
                            }
                          >
                            {translate(child.label)}
                          </span>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </AccordionDetails>
            </Accordion>
          )}

          {hasSizeChart && (
            <Accordion
              defaultExpanded={firstExpandedSection === 'measurements'}
              className={styles.accordion}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} className={styles.accordionSummary}>
                <div className={styles.sectionHeaderLeft}>
                  <h3 className={styles.sectionTitle}>{translate('Measurements')}</h3>
                  {Object.keys(measurementCards).length > 0 && (
                    <span className={styles.sectionCount}>
                      ({Object.keys(measurementCards).length})
                    </span>
                  )}
                </div>
              </AccordionSummary>
              <AccordionDetails className={styles.accordionDetails}>
                {Object.keys(measurementCards).length === 0 ? (
                  <p className={styles.empty}>{translate('No measurements')}</p>
                ) : (
                  <div className={styles.measurementCards}>
                    {Object.entries(measurementCards).map(([sizeKey, measurements], idx) => (
                      <MeasurementSize
                        key={sizeKey}
                        sizeKey={sizeKey}
                        measurements={measurements}
                        handleCardToggle={handleMeasurementCardToggle}
                        readOnly={true}
                        showDefectColumn={false}
                        idx={idx.toString()}
                        expandedCard={expandedMeasurementCard}
                        handleMeasurementChange={() => {}}
                        onAdd={() => {}}
                      />
                    ))}
                  </div>
                )}
              </AccordionDetails>
            </Accordion>
          )}

          {hasInspectionImagesModule ? (
            <Accordion
              defaultExpanded={firstExpandedSection === 'images'}
              className={styles.accordion}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} className={styles.accordionSummary}>
                <div className={styles.sectionHeaderRow}>
                  <div className={styles.sectionHeaderLeft}>
                    <h3 className={styles.sectionTitle}>{translate('Inspection Images')}</h3>
                    {inspectionImages.length > 0 && (
                      <span className={styles.sectionCount}>
                        {' '}
                        (
                        {selectedImageType === ALL_IMAGE_TYPES
                          ? inspectionImages.length
                          : `${visibleInspectionImages.length}/${inspectionImages.length}`}
                        )
                      </span>
                    )}
                  </div>

                  <div
                    className={styles.sectionHeaderRight}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    onFocus={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    <FormControl
                      size="small"
                      className={styles.imageTypeFilter}
                      disabled={inspectionImages.length === 0}
                    >
                      <Select
                        value={selectedImageType}
                        onChange={(e: SelectChangeEvent) => setSelectedImageType(e.target.value)}
                        sx={{
                          width: { xs: '100%', sm: 280 },
                          maxWidth: '100%',
                          '& .MuiSelect-select': {
                            fontSize: '0.7rem',
                            paddingTop: '2px',
                            paddingBottom: '2px',
                          },
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              minWidth: { xs: 'min(100vw - 2rem, 280px)', sm: 280 },
                              maxWidth: 'min(100vw - 1rem, 320px)',
                              fontSize: '0.7rem',
                              '& .MuiList-root': {
                                fontSize: '0.7rem',
                              },
                              '& .MuiMenuItem-root': {
                                fontSize: '0.7rem',
                              },
                            },
                          },
                        }}
                      >
                        {inspectionImageTypeOptions.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>
                </div>
              </AccordionSummary>
              <AccordionDetails className={styles.accordionDetails}>
                {inspectionImages.length === 0 ? (
                  <p className={styles.empty}>{translate('No inspection images')}</p>
                ) : (
                  <div className={styles.photoGrid}>
                    {visibleInspectionImages.map((img) => (
                      <PreviewImage key={img.id} img={img} inspectionId={inspection.id} />
                    ))}
                  </div>
                )}
              </AccordionDetails>
            </Accordion>
          ) : (
            <Accordion
              defaultExpanded={firstExpandedSection === 'images'}
              className={styles.accordion}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} className={styles.accordionSummary}>
                <h3 className={styles.sectionTitle}>{translate('Full Product Images')}</h3>
              </AccordionSummary>
              <AccordionDetails className={styles.accordionDetails}>
                {inspectionImages.length === 0 ? (
                  <p className={styles.empty}>{translate('No full product images')}</p>
                ) : (
                  <div className={styles.photoGrid}>
                    {sortedInspectionImages.map((img) => (
                      <PreviewImage
                        key={img.id}
                        img={img}
                        inspectionId={inspection.id}
                        showLabel={false}
                      />
                    ))}
                  </div>
                )}
              </AccordionDetails>
            </Accordion>
          )}

          <Accordion className={styles.accordion}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} className={styles.accordionSummary}>
              <div className={styles.sectionHeaderLeft}>
                <h3 className={styles.sectionTitle}>{translate('Defect Details')}</h3>
                {defects.length > 0 && (
                  <span className={styles.sectionCount}>({defects.length})</span>
                )}
              </div>
            </AccordionSummary>
            <AccordionDetails className={styles.accordionDetails}>
              <DefectDetailsTable defects={defects} inspectionId={inspection.id} />
            </AccordionDetails>
          </Accordion>

          <Accordion className={styles.accordion}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} className={styles.accordionSummary}>
              <h3 className={styles.sectionTitle}>{translate('Inspector Comments')}</h3>
            </AccordionSummary>
            <AccordionDetails className={styles.accordionDetails}>
              <p className={styles.comments}>
                {inspection.inspectorComment || translate('No comments')}
              </p>
            </AccordionDetails>
          </Accordion>
        </div>
        {footer != null && <div className={styles.footer}>{footer}</div>}
      </div>
    </div>
  );
};
